import { z } from 'zod';
import {
  countryCodeSchema,
  joinPolicySchema,
  orgTypeSchema,
  USER_CREATABLE_ORG_TYPES,
} from './orgs';
import { roleSchema } from './roles';
import { domainSchema } from './domains';
import { DEFAULT_PLAN, limitOverridesSchema, planIdSchema } from './plans';

/** Request bodies shared by the API (validation) and web app (forms). */

export const createOrgSchema = z.object({
  name: z.string().trim().min(2).max(120),
  type: z.enum(USER_CREATABLE_ORG_TYPES as [string, ...string[]]).pipe(orgTypeSchema),
  country: countryCodeSchema,
});
export type CreateOrgInput = z.infer<typeof createOrgSchema>;

export const updateOrgSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    country: countryCodeSchema,
    joinPolicy: joinPolicySchema,
  })
  .partial();
export type UpdateOrgInput = z.infer<typeof updateOrgSchema>;

export const addDomainSchema = z.object({ domain: domainSchema });
export type AddDomainInput = z.infer<typeof addDomainSchema>;

export const createInviteCodeSchema = z.object({
  role: roleSchema.default('learner'),
  groupId: z.string().uuid().optional(),
  maxUses: z.number().int().positive().max(100_000).optional(),
  expiresAt: z.coerce.date().optional(),
});
export type CreateInviteCodeInput = z.infer<typeof createInviteCodeSchema>;

// ─── Onboarding and platform admin ─────────────────────────────────────

export const orgRequestSchema = createOrgSchema.extend({
  website: z
    .string()
    .trim()
    .url()
    .max(200)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  domain: domainSchema.optional().or(z.literal('').transform(() => undefined)),
  expectedMembers: z.coerce.number().int().min(1).max(10_000_000),
  message: z.string().trim().max(2000).optional(),
});
export type OrgRequestInput = z.infer<typeof orgRequestSchema>;

export const approveOrgRequestSchema = z.object({
  planId: planIdSchema.default(DEFAULT_PLAN),
  limitOverrides: limitOverridesSchema.optional(),
  note: z.string().trim().max(2000).optional(),
});
export type ApproveOrgRequestInput = z.infer<typeof approveOrgRequestSchema>;

export const rejectOrgRequestSchema = z.object({
  note: z.string().trim().min(1, 'Tell the requester why').max(2000),
});
export type RejectOrgRequestInput = z.infer<typeof rejectOrgRequestSchema>;

/** Admin creates an org on a customer's behalf and names its owner by email. */
export const adminCreateOrgSchema = createOrgSchema.extend({
  planId: planIdSchema.default(DEFAULT_PLAN),
  limitOverrides: limitOverridesSchema.optional(),
  ownerEmail: z.string().trim().toLowerCase().email(),
});
export type AdminCreateOrgInput = z.infer<typeof adminCreateOrgSchema>;

export const creatorGrantSchema = z.object({
  maxOrgs: z.number().int().min(1).max(1000),
  planId: planIdSchema.default(DEFAULT_PLAN),
  reason: z.string().trim().max(500).optional(),
});
export type CreatorGrantInput = z.infer<typeof creatorGrantSchema>;

export const adminUpdateOrgSchema = z
  .object({
    planId: planIdSchema,
    limitOverrides: limitOverridesSchema.nullable(),
    archived: z.boolean(),
  })
  .partial();
export type AdminUpdateOrgInput = z.infer<typeof adminUpdateOrgSchema>;
